<script setup>
// ============================================================================
// BonBoZ: dòng "số liệu của trang nào" đứng đầu mọi trang Thống kê.
//
// Phạm vi của cả phần Thống kê là NGẦM ĐỊNH: nó đến từ trang người dùng đã mở
// hoặc đã gộp ở Bảng điều khiển, chứ không phải từ một bộ lọc nằm trên màn hình
// này. Không nói ra thì một con số nhỏ hơn kỳ vọng đọc thành "số bị sai" —
// không ai đoán được là đang lọc.
//
// Hiện LOGO + TÊN chứ không chỉ tên: hai trang của phòng khám đặt tên gần
// giống nhau, đọc lướt một dòng chữ thì không phân biệt nổi, còn ảnh đại diện
// thì nhận ra ngay từ đầu phòng. Ảnh cũng mang huy hiệu nền tảng (Facebook,
// Instagram...) nên không cần thêm icon riêng.
//
// Chọn "tất cả" vẫn liệt kê từng trang: câu "tất cả các trang" không trả lời
// được "vậy gồm những trang nào" — mà đó mới là câu người đọc đang hỏi.
// ============================================================================
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useSelectedPages } from 'dashboard/composables/useSelectedPages';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

// Nhiều hơn ngần này thì hàng chip dài hơn cả bảng số liệu nó đang chú thích.
// Phòng khám có 2–3 trang nên mốc này gần như không bao giờ chạm tới; nó ở đây
// để một tài khoản nhiều trang không làm vỡ bố cục.
const TOI_DA = 6;

const inboxes = useMapGetter('inboxes/getInboxes');
const { selectedInboxIds, isAllSelected } = useSelectedPages();

// Đi theo THỨ TỰ DANH SÁCH HỘP THƯ, không theo thứ tự bấm chọn: trang Thống kê
// nào cũng sắp trang theo tên, hai chỗ lệch nhau thì người đọc phải dò lại.
const trang = computed(() => {
  if (isAllSelected.value) return inboxes.value;
  const chon = new Set(selectedInboxIds.value.map(Number));
  return inboxes.value.filter(hop => chon.has(Number(hop.id)));
});

const hien = computed(() => trang.value.slice(0, TOI_DA));
const con_lai = computed(() => Math.max(0, trang.value.length - TOI_DA));

// Danh sách hộp thư có thể chưa nạp xong khi vào thẳng bằng URL. Lúc đó thà im
// còn hơn hiện "Số liệu của trang:" rồi bỏ trống chỗ tên.
const dang_cho_nap = computed(() => inboxes.value.length === 0);

// LỰA CHỌN CHẾT: hộp thư đã nạp rồi mà không id nào khớp — trang bị gỡ, hoặc
// nối lại và Chatwoot cấp id mới. Bộ lọc vẫn gửi id cũ xuống nên MỌI con số
// trên màn hình về 0 một cách hoàn toàn hợp lệ. Im lặng ở đây là kiểu tệ nhất:
// người đọc thấy bảng rỗng và kết luận "tháng này không có khách nào".
const lua_chon_chet = computed(
  () => !dang_cho_nap.value && !isAllSelected.value && trang.value.length === 0
);

const hien_dong = computed(() => lua_chon_chet.value || trang.value.length > 0);
</script>

<template>
  <div
    v-if="hien_dong"
    class="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs"
    :class="lua_chon_chet ? 'text-n-ruby-11' : 'text-n-slate-11'"
  >
    <span v-if="lua_chon_chet" class="flex items-center gap-1.5">
      <span
        class="i-lucide-triangle-alert size-3.5 shrink-0"
        aria-hidden="true"
      />
      {{ $t('BONBOZ.PHAM_VI.KHONG_CON') }}
    </span>

    <span v-else class="flex items-center gap-1.5">
      <span class="i-lucide-filter size-3.5 shrink-0" aria-hidden="true" />
      {{
        isAllSelected ? $t('BONBOZ.PHAM_VI.TAT_CA') : $t('BONBOZ.PHAM_VI.TRANG')
      }}
    </span>

    <span
      v-for="hop in hien"
      :key="hop.id"
      class="flex items-center gap-1.5 py-0.5 pe-2.5 ps-0.5 rounded-full border border-[rgb(var(--pk-vien))] bg-n-background"
    >
      <Avatar
        :src="hop.avatar_url"
        :name="hop.name"
        :inbox="hop"
        :size="20"
        rounded-full
        class="shrink-0"
      />
      <span class="font-medium truncate max-w-[14rem] text-n-slate-12">
        {{ hop.name }}
      </span>
    </span>

    <span v-if="con_lai">{{ $t('BONBOZ.PHAM_VI.THEM', { so: con_lai }) }}</span>
  </div>
</template>
